import { ImageBlock } from "@/libs/builder/blocks/ImageBlock";
import { BuilderBlockProps } from "@/types";
import { ImageLoaderProps } from "next/image";

const DEFAULT_BLUR = 0.5;
export const imageLoader =
  (props: Omit<ImageBlock, keyof BuilderBlockProps>) =>
  ({ src, quality, width }: ImageLoaderProps) => {
    const {
      width: providedWidth,
      height: providedHeight,
      fit,
      upscale,
      crop,
      trim,
      orient,
      brightness,
      contrast,
      saturation,
      blur,
    } = props;
    const url = new URL(src);

    // webp
    url.searchParams.set("auto", "webp");
    url.searchParams.set("format", "webply");

    // intrinsic width is not known yet
    if (!props.intrinsicWidth && !props.intrinsicHeight) {
      // width
      if (Number.isSafeInteger(providedWidth)) {
        url.searchParams.set("width", String(providedWidth));
      }

      // height
      if (Number.isSafeInteger(providedHeight)) {
        url.searchParams.set("height", String(providedHeight));
      }
    } else {
      const minimumWidth = Math.min(width, props.intrinsicWidth || Infinity);

      // width
      if (Number.isSafeInteger(minimumWidth)) {
        url.searchParams.set("width", String(minimumWidth));
      }
      const aspectRatio =
        Number(props.intrinsicWidth) / Number(props.intrinsicHeight);

      // height
      const height = Math.ceil(minimumWidth / aspectRatio);
      if (Number.isSafeInteger(height)) {
        url.searchParams.set("height", String(height));
      }
    }

    if (
      Number.isSafeInteger(providedWidth) &&
      Number.isSafeInteger(providedHeight) &&
      fit
    ) {
      url.searchParams.set("fit", fit);
    }

    // upscale
    const enableUpscale = upscale;
    if (enableUpscale) {
      url.searchParams.set("enable", "upscale");
    } else {
      url.searchParams.set("disable", "upscale");
    }

    if (quality) {
      url.searchParams.set("quality", String(quality));
    }

    // crop
    const cropType = crop?.type;
    const cropStartPositionType = crop?.cropStartPosition;

    let cropSize;
    let cropStartPosition;

    if (cropType === "pixels") {
      const widthPixels = crop?.widthPixels;
      const heightPixels = crop?.heightPixels;

      if (
        Number.isSafeInteger(widthPixels) &&
        Number.isSafeInteger(heightPixels)
      ) {
        cropSize = `${widthPixels},${heightPixels}`;
      }
    } else if (cropType === "ratio") {
      const widthRatio = crop?.widthRatio;
      const heightRatio = crop?.heightRatio;

      if (
        Number.isSafeInteger(widthRatio) &&
        Number.isSafeInteger(heightRatio)
      ) {
        cropSize = `${widthRatio}:${heightRatio}`;
      }
    }

    if (cropStartPositionType === "pixels") {
      const x = crop?.x;
      const y = crop?.y;

      if (Number.isSafeInteger(x) && Number.isSafeInteger(y)) {
        cropStartPosition = `x${x},y${y}`;
      }
    } else if (cropStartPositionType === "percentage") {
      const xOffset = crop?.xOffset;
      const yOffset = crop?.yOffset;

      if (Number.isSafeInteger(xOffset) && Number.isSafeInteger(yOffset)) {
        cropStartPosition = `offset-x${xOffset},offset-y${yOffset}`;
      }
    }

    if (cropStartPosition) {
      url.searchParams.set("crop", `${cropSize},${cropStartPosition}`);
    } else if (cropSize) {
      url.searchParams.set("crop", `${cropSize}`);
    }

    if (
      Number.isSafeInteger(trim?.top) &&
      Number.isSafeInteger(trim?.right) &&
      Number.isSafeInteger(trim?.bottom) &&
      Number.isSafeInteger(trim?.left)
    ) {
      url.searchParams.set(
        "trim",
        `${trim?.top},${trim?.right},${trim?.bottom},${trim?.left}`,
      );
    }

    if (orient) {
      url.searchParams.set("orient", String(orient));
    }

    if (Number.isSafeInteger(blur) && blur !== DEFAULT_BLUR) {
      url.searchParams.set("blur", String(blur));
    }

    if (brightness) {
      url.searchParams.set("brightness", String(brightness));
    }

    if (contrast) {
      url.searchParams.set("contrast", String(contrast));
    }

    if (saturation) {
      url.searchParams.set("saturation", String(saturation));
    }

    return url.toString();
  };
